import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import ClientService from "../services/client.service";
import {
  loadCollectJsScript,
  normalizePayrocTokenizationKey,
} from "../config/payroc.ts";
import type { AppointmentConfirmationData } from "./models";

declare global {
  interface Window {
    CollectJS: {
      configure: (config: Record<string, unknown>) => void;
      startPaymentRequest: () => void;
    };
  }
}

const ClientConfirmation = () => {
  const { id } = useParams<{ id: string }>();
  const [status, setStatus] = useState<string | null>(null);
  const [appointmentData, setAppointmentData] = useState<AppointmentConfirmationData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const tokenizationKey = normalizePayrocTokenizationKey(
    appointmentData?.tokenizationKey,
  );

  const getApiError = (err: unknown) => {
    if (typeof err !== "object" || err === null) {
      return null;
    }

    return err as {
      response?: {
        status?: number;
        data?: {
          errors?: {
            nonFieldErrors?: string[];
          };
        };
      };
    };
  };

  const ErrorLabel = () => {
    if (error) {
      if (error === "wrong status") {
        return (
          <div>
            Appointment in wrong status, did you already confirm it? If not,
            please contact us
          </div>
        );
      } else if (error === "already used") {
        return <div>Token to confirm appointment was already used</div>;
      } else if (error === "not found") {
        return <div>Appointment not found, please contact us</div>;
      } else {
        return <div>An error occured: {error}</div>;
      }
    } else {
      return <div>System is checking your confirmation</div>;
    }
  };

  const confirm = useCallback(async (token: string) => {
    try {
      const data = {
        token: token,
      };
      const response = await ClientService.confirmAppointment(id, data);
      if (response.status === 200) {
        setStatus("paid");
      } else {
      }
    } catch (error) {
      setStatus("error");
      const apiError = getApiError(error);
      if (apiError?.response?.status === 400) {
        setError(apiError.response.data?.errors?.nonFieldErrors?.[0] ?? "error");
      } else if (apiError?.response?.data) {
        console.log(apiError.response.data);
      }
    }
  }, [id]);

  const loadCollectJs = useCallback(() => {
    if (!tokenizationKey) {
      setError("Payment is not configured for this account.");
      return;
    }

    window.CollectJS.configure({
      variant: "lightbox",
      buttonText: "Pay $100 to confirm appointment",
      instructionText: "Enter Card Info Below",
      price: "100.00",
      currency: "USD",
      country: "US",
      callback: (token: { token: string }) => {
        setIsSubmitting(true);
        confirm(token.token);
      },
    });
  }, [confirm, tokenizationKey]);

  useEffect(() => {
    const getAppointment = async () => {
      if (!id) {
        setError("not found");
        return;
      }

      try {
        const response = await ClientService.getAppointment(id);
        if (response.status === 200) {
          setStatus("nonpaid");
          setAppointmentData(response.data.data);
        }
      } catch (error) {
        const apiError = getApiError(error);
        if (apiError?.response?.status === 400) {
          setError(apiError.response.data?.errors?.nonFieldErrors?.[0] ?? "error");
        } else if (apiError?.response?.data) {
          console.log(apiError.response.data);
        }
      }
    };
    getAppointment();
  }, [id]);

  useEffect(() => {
    if (!tokenizationKey) {
      return;
    }

    return loadCollectJsScript(tokenizationKey, loadCollectJs);
  }, [loadCollectJs, tokenizationKey]);

  const handleSubmit = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!tokenizationKey) {
      setError("Payment is not configured for this account.");
      return;
    }
    window.CollectJS.startPaymentRequest();
  };

  const InnerForm = () => {
    if (status === "nonpaid" && appointmentData) {
      return (
        <div style={stylingObject.center}>
          <div style={stylingObject.center}>
            Dear {appointmentData.appointmentClient}
          </div>
          <div>
            Your appointment is set to: <br />
            <b>{moment(appointmentData.appointmentDate).format("LLL")}</b>
          </div>
          <div style={stylingObject.payButton}>
            <Button
              type="submit"
              variant="danger"
              onClick={handleSubmit}
              color={"red"}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                <span>Pay to Confirm Appointment</span>
              )}
            </Button>
          </div>
        </div>
      );
    } else if (status === "paid" && appointmentData) {
      return (
        <div>
          <div>Dear {appointmentData.appointmentClient}</div>
          <div>
            Thank you. Your appointment is confirmed to <br />
            <b>{moment(appointmentData.appointmentDate).format("LLL")}</b>
          </div>
        </div>
      );
    } else {
      return (
        <div>
          <ErrorLabel />
        </div>
      );
    }
  };

  return (
    <div className="col-md-12">
      <div className="card card-container" style={stylingObject.center}>
        <img
          src="https://lirp.cdn-website.com/a85f75f8/dms3rep/multi/opt/LayoutsDB67747D-C218-0AC4-C1B8-2184CDD25E0F.png_400-356w.png"
          alt="profile-img"
          className="profile-img-card"
          width={200}
        />
        <InnerForm />
      </div>
    </div>
  );
};

const stylingObject: Record<"center" | "payButton", React.CSSProperties> = {
  center: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  payButton: {
    padding: 10,
  },
};

export default ClientConfirmation;
